import { useState } from "react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function Book() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", condition: "", message: "", source: "website" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/public", { ...form, stage: "new" });
      toast.success("Request received. Our team will confirm a slot shortly.");
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <MarketingHeader />
      <section className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)] mb-3">Book an appointment</p>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">Start your healing journey.</h1>
        <p className="text-[color:var(--muted)] mb-10 leading-relaxed">
          Fill in the form below. We will get back to you within a few hours to confirm a consultation slot
          (in-person at Mumbai or online).
        </p>

        {submitted ? (
          <div className="bg-white border border-[color:var(--border)] rounded-2xl p-10 text-center" data-testid="booking-success">
            <h2 className="font-heading text-2xl text-[color:var(--ink)] mb-2">Request received</h2>
            <p className="text-[color:var(--muted)]">Our team will confirm your slot shortly. Thank you for choosing Dr Harleen Homeopathy Clinic.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[color:var(--border)] rounded-2xl p-7 md:p-9 space-y-5" data-testid="booking-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Full name *</label>
                <Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-2" data-testid="book-name" />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Phone *</label>
                <Input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-2" data-testid="book-phone" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Email</label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-2" data-testid="book-email" />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">How did you hear about us?</label>
                <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}>
                  <SelectTrigger className="mt-2" data-testid="book-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="referral">Friend / Referral</SelectItem>
                    <SelectItem value="google">Google search</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Primary concern</label>
              <Input value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} className="mt-2" placeholder="e.g. Migraine, PCOS, Eczema" data-testid="book-condition" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Anything else?</label>
              <Textarea rows={4} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="mt-2" data-testid="book-message" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-12" data-testid="book-submit">
              {loading ? "Submitting…" : "Request appointment"}
            </Button>
          </form>
        )}
      </section>
      <MarketingFooter />
    </div>
  );
}
